import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import MovieCard from "@/components/feature/MovieCard";
import { filterMovies, type FilterOptions } from "@/utils/movies";
import FilterBar from "@/pages/category/components/FilterBar";
import Pagination from "@/pages/category/components/Pagination";

const PAGE_SIZE = 20;

const DEFAULTS = {
  type: "全部",
  region: "全部",
  year: "全部",
  genre: "全部",
  sort: "update",
};

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();

  const current: Required<Omit<FilterOptions, "sort">> & { sort: string } = {
    type: searchParams.get("type") || DEFAULTS.type,
    region: searchParams.get("region") || DEFAULTS.region,
    year: searchParams.get("year") || DEFAULTS.year,
    genre: searchParams.get("genre") || DEFAULTS.genre,
    sort: searchParams.get("sort") || DEFAULTS.sort,
  };

  const page = Math.max(1, Number(searchParams.get("page") || "1"));

  const results = useMemo(() => filterMovies(current), [current.type, current.region, current.year, current.genre, current.sort]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = results.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const heading = current.type !== "全部" ? `${current.type}分类` : "影视分类";
  const metaTitle =
    current.type !== "全部"
      ? `${current.type}分类 - 热情的邻居影视内容`
      : "影视分类 - 热情的邻居影视内容";

  usePageMeta({
    title: metaTitle,
    description: `热情的邻居影视分类页，当前分类共 ${results.length} 部作品，可按类型、地区、年份与题材标签筛选，快速找到感兴趣的影视内容。`,
    keywords: `热情的邻居,影视分类,${current.type},影视筛选,电视剧集,影视推荐`,
  });

  const updateParams = (patch: Partial<FilterOptions & { page: number }>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value || value === "全部" || (key === "sort" && value === DEFAULTS.sort)) {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    if (!("page" in patch)) next.delete("page");
    setSearchParams(next);
  };

  const resetFilters = () => setSearchParams(new URLSearchParams());

  const goPage = (next: number) => {
    const params = new URLSearchParams(searchParams);
    if (next <= 1) params.delete("page");
    else params.set("page", String(next));
    setSearchParams(params);
  };

  useEffect(() => {
    if (page !== safePage) {
      goPage(safePage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, safePage]);

  return (
    <div className="w-full px-4 md:px-10 py-8 md:py-12">
      <div className="max-w-7xl mx-auto">
        <nav className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-500 mb-4">
          <Link to="/" className="hover:text-accent-500 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-200">影视分类</span>
          {current.type !== "全部" && (
            <>
              <i className="ri-arrow-right-s-line" />
              <span className="text-accent-500">{current.type}</span>
            </>
          )}
        </nav>

        <h1 className="font-heading font-black text-2xl md:text-4xl text-foreground-50">{heading}</h1>
        <p className="mt-3 text-sm md:text-base text-foreground-400 max-w-3xl leading-relaxed">
          按类型、地区、年份与题材标签组合筛选，浏览“热情的邻居”相关的电视剧、电影、综艺与纪录片内容，
          每个条目均可进入详情页查看完整剧情介绍。
        </p>

        <div className="mt-6 md:mt-8">
          <FilterBar
            value={current}
            onChange={updateParams}
            onReset={resetFilters}
            resultCount={results.length}
          />
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5 mt-8">
              {pageItems.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <Pagination page={safePage} totalPages={totalPages} onChange={goPage} />
          </>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center text-center py-16 bg-background-100 border border-background-200/60 rounded-lg">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 mb-4">
              <i className="ri-search-eye-line text-3xl" />
            </span>
            <h3 className="font-heading font-bold text-lg text-foreground-100">没有找到符合条件的影视内容</h3>
            <p className="text-sm text-foreground-500 mt-2 max-w-md">
              试试减少筛选条件，或点击下方按钮重置全部筛选，重新浏览影视列表。
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 inline-flex items-center gap-2 px-5 h-10 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        )}
      </div>
    </div>
  );
}